import { Button } from '@ipfabric/ui-components/Button/Button';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import {
  type Filter,
  type TableColumn,
  createTableFilterLinkProps,
  createTextColumn,
} from '@ipfabric/utils';
import type { DebugLogEntry } from '@ipfabric/utils/pathLookup/DebugPathlookup';
import classNames from 'classnames';
import { ButtonAsA } from 'components/Button/ButtonAsA';
import type { DeviceViewPathlookupDebugEntity } from 'components/DeviceView/deviceViewTypes';
import { Table } from 'components/Table/components/Table';
import { useFrontendLocalTable } from 'components/Table/model/useFrontendLocalTable';
import { type FC, memo, useEffect, useMemo, useState } from 'react';

import { DeviceViewPathlookupDebugModal } from './DeviceViewPathlookupDebugModal';

interface Props {
  deviceViewEntity: DeviceViewPathlookupDebugEntity;
  visible: boolean;
}

interface EntryDebugRow {
  id: string;
  hostname: string | null;
  vDeviceId: string | null;
  messageType: string | null;
  tag: string | null;
  message: string | null;
  callStack: string | null;
  logData: string | null;
}

const getFallback = (value?: string | null): string | null => value ?? null;

const normalizeCallStack = (entry: DebugLogEntry): string | null => {
  if (Array.isArray(entry.callStack)) {
    return entry.callStack.join(', ');
  }
  return entry.callStack ?? null;
};

const normalizeLogData = (entry: DebugLogEntry): string | null => {
  if (!entry.logData) {
    return null;
  }
  try {
    return JSON.stringify(entry.logData, null, 2);
  } catch {
    return '[Unable to stringify logData]';
  }
};

const createEqFilter = (value: string): Filter => ['eq', value];

const BareDeviceViewPathlookupDebug: FC<Props> = (props) => {
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [openedLogData, setOpenedLogData] = useState<string | null>(null);
  const [openedCallStack, setOpenedCallStack] = useState<string | null>(null);

  const entryRows: EntryDebugRow[] = useMemo(
    () =>
      props.deviceViewEntity.entries.map((entry, index) => ({
        id: `${entry.vDeviceId ?? 'unknown'}-${index}`,
        hostname: getFallback(entry.hostname),
        vDeviceId: getFallback(entry.vDeviceId),
        messageType: getFallback(entry.messageType),
        tag: getFallback(entry.tag),
        message: getFallback(entry.message),
        callStack: normalizeCallStack(entry),
        logData: normalizeLogData(entry),
      })),
    [props.deviceViewEntity.entries],
  );

  const selectedDevice =
    selectedDeviceId === null
      ? null
      : (props.deviceViewEntity.debugDevices[selectedDeviceId] ?? null);

  const selectedEntryRows = useMemo(
    () =>
      selectedDeviceId === null
        ? []
        : entryRows.filter((entry) => entry.vDeviceId === selectedDeviceId),
    [entryRows, selectedDeviceId],
  );

  useEffect(() => {
    if (
      selectedDeviceId !== null &&
      !entryRows.some((entry) => entry.vDeviceId === selectedDeviceId)
    ) {
      setSelectedDeviceId(null);
    }
  }, [entryRows, selectedDeviceId]);

  const renderLogData = ({ row }: { row: EntryDebugRow }) => {
    if (row.logData === null) {
      return { content: null };
    }

    return {
      content: (
        <Tooltip
          content={
            <div className='max-h-[60vh] max-w-[80vw] overflow-auto whitespace-pre-wrap break-all font-mono text-xs'>
              {row.logData}
            </div>
          }
        >
          <ButtonAsA
            className='block w-full cursor-pointer overflow-hidden text-left text-current no-underline hover:no-underline'
            onClick={() => setOpenedLogData(row.logData)}
          >
            {row.logData}
          </ButtonAsA>
        </Tooltip>
      ),
    };
  };

  const renderCallStack = ({ row }: { row: EntryDebugRow }) => {
    if (row.callStack === null) {
      return { content: null };
    }

    return {
      content: (
        <Tooltip
          content={
            <div className='max-h-[60vh] max-w-[80vw] overflow-auto whitespace-pre-wrap break-all font-mono text-xs'>
              {row.callStack}
            </div>
          }
        >
          <ButtonAsA
            className='block w-full cursor-pointer overflow-hidden text-left text-current no-underline hover:no-underline'
            onClick={() => setOpenedCallStack(row.callStack)}
          >
            {row.callStack}
          </ButtonAsA>
        </Tooltip>
      ),
    };
  };

  const deviceColumns: TableColumn<EntryDebugRow>[] = [
    createTextColumn({
      key: 'vDeviceId',
      title: 'vDeviceId',
      hidden: true,
    }),
    createTextColumn({
      key: 'hostname',
      title: 'Hostname',
      renderContent: ({ row }: { row: EntryDebugRow }) => ({
        content:
          row.vDeviceId === null ? (
            row.hostname
          ) : (
            <ButtonAsA
              className='text-link-legacy'
              onClick={() => setSelectedDeviceId(row.vDeviceId)}
            >
              {row.hostname}
            </ButtonAsA>
          ),
      }),
    }),
    createTextColumn({
      key: 'messageType',
      title: 'Type',
    }),
    createTextColumn({
      key: 'tag',
      title: 'Tag',
    }),
    createTextColumn({
      key: 'message',
      title: 'Message',
      multiline: true,
    }),
    createTextColumn({
      key: 'callStack',
      title: 'Call Stack',
      multiline: true,
      renderContent: renderCallStack,
    }),
    createTextColumn({
      key: 'logData',
      title: 'Log Data',
      multiline: true,
      renderContent: renderLogData,
    }),
  ];

  const entryColumns: TableColumn<EntryDebugRow>[] = [
    createTextColumn({
      key: 'hostname',
      title: 'Hostname',
    }),
    createTextColumn({
      key: 'vDeviceId',
      title: 'vDeviceId',
      hidden: true,
    }),
    createTextColumn({
      key: 'messageType',
      title: 'Type',
      hidden: true,
    }),
    createTextColumn({
      key: 'tag',
      title: 'Tag',
    }),
    createTextColumn({
      key: 'message',
      title: 'Message',
      multiline: true,
    }),
    createTextColumn({
      key: 'callStack',
      title: 'Call Stack',
      multiline: true,
      renderContent: renderCallStack,
    }),
    createTextColumn({
      key: 'logData',
      title: 'Log Data',
      multiline: true,
      renderContent: renderLogData,
    }),
  ];

  const deviceTableSimpleFilter = useFrontendLocalTable({
    columns: deviceColumns,
    data: entryRows,
  });

  const detailTableSimpleFilter = useFrontendLocalTable({
    columns: entryColumns,
    data: selectedEntryRows,
  });

  const selectedHostname =
    selectedDevice?.hostname ?? selectedEntryRows[0]?.hostname ?? null;

  const connectivityMatrixLink =
    selectedHostname === null
      ? null
      : createTableFilterLinkProps(
          '/technology/interfaces/connectivity-matrix/connectivity-matrix',
          () => ({
            localHost: createEqFilter(selectedHostname),
          }),
          {},
        ).to;

  const devicesLink =
    selectedHostname === null
      ? null
      : createTableFilterLinkProps(
          '/inventory/devices',
          () => ({
            hostname: createEqFilter(selectedHostname),
          }),
          {},
        ).to;

  return (
    <div
      className={classNames(
        'flex flex-1 flex-col bg-gray-50',
        !props.visible && 'hidden',
      )}
    >
      <div className='flex flex-1 flex-col gap-2 p-2'>
        {selectedDeviceId === null ? (
          <Table
            borderless
            className='flex-1'
            columns={deviceColumns}
            data={deviceTableSimpleFilter.data}
            noDataBox='No debug entries available.'
            onChangeFilter={deviceTableSimpleFilter.onChangeFilter}
            onRemoveFilters={deviceTableSimpleFilter.onRemoveFilters}
            onSubmitFilters={deviceTableSimpleFilter.onSubmitFilters}
            rowIdField='id'
            simpleFilterFields={deviceTableSimpleFilter.simpleFilterFields}
            simpleFilterFieldsSubmitted={
              deviceTableSimpleFilter.simpleFilterFieldsSubmitted
            }
            title='Debug Devices'
          />
        ) : (
          <>
            <div className='rounded bg-white px-3 py-2 text-sm'>
              <div className='mb-1'>
                <ButtonAsA
                  className='text-link-legacy'
                  onClick={() => setSelectedDeviceId(null)}
                >
                  Back to devices
                </ButtonAsA>
              </div>
              <div className='grid grid-cols-1 gap-1 md:grid-cols-2'>
                <div>
                  <strong>Hostname:</strong> {selectedDevice?.hostname ?? '-'}
                </div>
                <div>
                  <strong>vDeviceId:</strong> {selectedDeviceId}
                </div>
                <div>
                  <strong>SN:</strong> {selectedDevice?.sn ?? '-'}
                </div>
                <div>
                  <strong>Device Type:</strong> {selectedDevice?.devType ?? '-'}
                </div>
                <div>
                  <strong>Group By:</strong>{' '}
                  {selectedDevice?.groupByValue ?? '-'}
                </div>
              </div>
            </div>
            <Table
              borderless
              className='flex-1'
              columns={entryColumns}
              data={detailTableSimpleFilter.data}
              menuItems={[
                {
                  standalone: true,
                  elementWithKey: (
                    <Tooltip
                      key='connectivityMatrixAction'
                      content='Device Connectivity Matrix'
                    >
                      <Button
                        type='button'
                        variant='outline'
                        size='small'
                        icon='fa-table-cells'
                        disabled={connectivityMatrixLink === null}
                        onClick={() => {
                          if (connectivityMatrixLink !== null) {
                            window.open(
                              connectivityMatrixLink,
                              '_blank',
                              'noopener,noreferrer',
                            );
                          }
                        }}
                        aria-label='Connectivity Matrix'
                      />
                    </Tooltip>
                  ),
                },
                {
                  standalone: true,
                  elementWithKey: (
                    <Tooltip
                      key='hostnameDevicesAction'
                      content='Hostname devices'
                    >
                      <Button
                        type='button'
                        variant='outline'
                        size='small'
                        icon='fa-desktop'
                        disabled={devicesLink === null}
                        onClick={() => {
                          if (devicesLink !== null) {
                            window.open(
                              devicesLink,
                              '_blank',
                              'noopener,noreferrer',
                            );
                          }
                        }}
                        aria-label='Hostname devices'
                      />
                    </Tooltip>
                  ),
                },
              ]}
              noDataBox='No debug entries for selected device.'
              onChangeFilter={detailTableSimpleFilter.onChangeFilter}
              onRemoveFilters={detailTableSimpleFilter.onRemoveFilters}
              onSubmitFilters={detailTableSimpleFilter.onSubmitFilters}
              rowIdField='id'
              simpleFilterFields={detailTableSimpleFilter.simpleFilterFields}
              simpleFilterFieldsSubmitted={
                detailTableSimpleFilter.simpleFilterFieldsSubmitted
              }
              title={`Debug ${selectedDevice?.hostname ?? selectedDeviceId}`}
            />
          </>
        )}
      </div>
      <DeviceViewPathlookupDebugModal
        content={openedLogData}
        onClose={() => {
          setOpenedLogData(null);
        }}
      />
      <DeviceViewPathlookupDebugModal
        content={
          openedCallStack === null
            ? null
            : openedCallStack.split(', ').join('\n')
        }
        onClose={() => {
          setOpenedCallStack(null);
        }}
      />
    </div>
  );
};

export const DeviceViewPathlookupDebug = memo(BareDeviceViewPathlookupDebug);
